import React from "react";
import { Link } from "react-router-dom";
import TableCell from "@material-ui/core/TableCell";
import numberFormatter from "utils/numberFormatter";
import LoanTableOptionsMenu from "components/Global/OptionsMenu/LoanTable/LoanTableOptionsMenu";
import _ from "lodash";

import { calculateClosingCosts } from "./util";

const WinningQuoteTableColumns = [
  {
    id: "e0f5bac6-3e28-11ea-8252-00155d015112",
    isBaseColumn: true,
    dataField: "lenderCompany.name",
    numeric: false,
    label: "Lender",
    Cell: ({ record, callbacks }) => (
      <TableCell>
        {(
          <Link to={`/loan/${callbacks.getDealId()}/${record.id}`}>
            {record["lenderCompany.name"]}
          </Link>
        ) || "\u2014"}
      </TableCell>
    )
  },
  {
    id: "e0f5bad4-3e28-11ea-8252-00155d015112",
    isBaseColumn: false,
    dataField: "amount",
    numeric: false,
    label: "Total Loan Amount",
    Cell: ({ record }) => (
      <TableCell>
        {numberFormatter.maskCurrency(record.amount) || "\u2014"}
      </TableCell>
    )
  },
  {
    id: "e0f5bad9-3e28-11ea-8252-00155d015112",
    isBaseColumn: false,
    dataField: "initialProceeds",
    numeric: false,
    label: "Initial Proceeds",
    Cell: ({ record }) => (
      <TableCell>
        {numberFormatter.maskCurrency(record.initialProceeds) || "\u2014"}
      </TableCell>
    )
  },
  {
    id: "e0f5badd-3e28-11ea-8252-00155d015112",
    isBaseColumn: false,
    dataField: "covenants.ltvAsIs",
    numeric: false,
    label: "LTV",
    Cell: ({ record }) => {
      let value = record["covenants.ltvAsIs"];
      if (!value) {
        value = record["covenants.ltvStabilized"];
      }

      return (<TableCell>
        {numberFormatter.formatRate(value, 2, "-") || "\u2014"}
              </TableCell>);
    }
  },
  {
    id: "e0f5bbb8-3e28-11ea-8252-00155d015112",
    isBaseColumn: false,
    dataField: "loanTermMonths",
    numeric: false,
    label: "Term",
    Cell: ({ record }) => (
      <TableCell>{record.loanTermMonths || "\u2014"}</TableCell>
    )
  },
  {
    id: "e0f5bbc4-3e28-11ea-8252-00155d015112",
    isBaseColumn: false,
    dataField: "extensionTerms.extensionTermMonths",
    numeric: false,
    label: "Extensions",
    Cell: ({ record }) => {
      const extensionOptions = _.get(record, "extensionTerms.extensionOptions");
      const extensionTermMonths = _.get(record, "extensionTerms.extensionTermMonths");
      if (!extensionOptions || !extensionTermMonths) {
        return "\u2014";
      }

      return (
        <TableCell>{`${extensionOptions}, ${extensionTermMonths} month extensions`}</TableCell>
      );
    }
  },
  {
    id: "e0f5bbd8-3e28-11ea-8252-00155d015112",
    isBaseColumn: false,
    dataField: "loanPricing",
    numeric: false,
    label: "Loan Pricing",
    Cell: ({ record }) => <TableCell>{ _.get(record, "loanPricing", "\u2014")}</TableCell>
  },
  {
    id: "e0f5bbe1-3e28-11ea-8252-00155d015112",
    isBaseColumn: false,
    dataField: "currentRate",
    numeric: false,
    label: "Current Rate",
    Cell: ({ record }) => (
      <TableCell>
        {numberFormatter.formatRate(record.currentRate, 2, "\u2014") }
      </TableCell>
    )
  },
  {
    id: "e0f5bbea-3e28-11ea-8252-00155d015112",
    isBaseColumn: false,
    dataField: "amortizationTerm",
    numeric: false,
    label: "Amortization Term",
    Cell: ({ record }) => (
      <TableCell>{record.amortizationTerm || "\u2014"}</TableCell>
    )
  },
  {
    id: "e0f5bbfa-3e28-11ea-8252-00155d015112",
    isBaseColumn: false,
    dataField: "ioPeriod",
    numeric: false,
    label: "I/O Term",
    Cell: ({ record }) => <TableCell>{record.ioPeriod || "\u2014"}</TableCell>
  },
  {
    id: "e0f5bc06-3e28-11ea-8252-00155d015112",
    isBaseColumn: false,
    dataField: "interestOnlyPayment",
    numeric: false,
    label: "I/O Payment",
    Cell: ({ record }) => <TableCell>{ numberFormatter.maskCurrency(record.interestOnlyPayment) || "\u2014"}</TableCell>
  },
  {
    id: "e0f5bc11-3e28-11ea-8252-00155d015112",
    isBaseColumn: false,
    dataField: "amortizingPayment",
    numeric: false,
    label: "Amortizing Payment",
    Cell: ({ record }) => (
      <TableCell>
        {numberFormatter.maskCurrency(record.amortizingPayment) || "\u2014"}
      </TableCell>
    )
  },
  {
    id: "e0f5bc19-3e28-11ea-8252-00155d015112",
    isBaseColumn: false,
    dataField: "hedgeRequirements.requiredHedge.value",
    numeric: false,
    label: "Hedge Type",
    Cell: ({ record }) => (
      <TableCell>{_.get(record, "hedgeRequirements.requiredHedge.value", "\u2014")}</TableCell>
    )
  },
  {
    id: "e0f5bc22-3e28-11ea-8252-00155d015112",
    isBaseColumn: false,
    dataField: "hedgeRequirements.marketCost",
    numeric: false,
    label: "Hedge Premium",
    Cell: ({ record }) => {
      const hedgeType = _.get(record, "hedgeRequirements.requiredHedge.value", "\u2014");
      if (hedgeType === "Cap") {
        return (<TableCell>{numberFormatter.maskCurrency(_.get(record, "hedgeRequirements.marketCost"), "\u2014", true)}</TableCell>);
      } else if (hedgeType === "Swap") {
        return (<TableCell>{numberFormatter.formatRate(_.get(record, "hedgeRequirements.swapRate"), 2, "\u2014") }</TableCell>);
      }

      return (<TableCell>{"\u2014"}</TableCell>);
    }
  },
  {
    id: "e0f5bc2a-3e28-11ea-8252-00155d015112",
    isBaseColumn: false,
    dataField: "closingCosts.totalAmount",
    numeric: false,
    label: "Closing Costs",
    Cell: ({ record }) => <TableCell>{numberFormatter.maskCurrency(_.get(record, "closingCosts.totalAmount", "\u2014"))}</TableCell>
  },
  {
    id: "e0f5bc48-3e28-11ea-8252-00155d015112",
    isBaseColumn: false,
    dataField: "loanRecourse.recourseType.name",
    numeric: false,
    label: "Recourse",
    Cell: ({ record }) => (
      <TableCell>{_.get(record, "loanRecourse.recourseType.name", "\u2014")}</TableCell>
    )
  },
  {
    id: "e0f5bc52-3e28-11ea-8252-00155d015112",
    isBaseColumn: false,
    dataField: "prepaymentTerms.prepaymentType.value",
    numeric: false,
    label: "Prepayment Type",
    Cell: ({ record }) => (
      <TableCell>{_.get(record, "prepaymentTerms.prepaymentType.value", "\u2014")}</TableCell>
    )
  },
  {
    id: "e0f5b832-3e28-11ea-8252-00155d015112",
    isBaseColumn: false,
    dataField: "covenants.dscrStabilized",
    numeric: false,
    label: "DSCR",
    Cell: ({ record }) => (
      <TableCell>{record["covenants.dscrStabilized"] ? `${record["covenants.dscrStabilized"]}x` : "\u2014"}</TableCell>
    )
  },
  {
    id: "e0f5b862-3e28-11ea-8252-00155d015112",
    isBaseColumn: false,
    dataField: "covenants.debtYieldStabilized",
    numeric: false,
    label: "Debt Yield",
    Cell: ({ record }) => (
      <TableCell>
        {numberFormatter.formatRate(record["covenants.debtYieldStabilized"], 2) || "\u2014"}
      </TableCell>
    )
  },
  {
    id: "e0f5b868-3e28-11ea-8252-00155d015112",
    isBaseColumn: false,
    dataField: "effectiveRate",
    numeric: false,
    label: "Effective Rate",
    Cell: ({ record }) => <TableCell>{numberFormatter.formatRate(_.get(record, "effectiveRate", null), 2, "\u2014")}</TableCell>
  },
  {
    id: "e0f5b86d-3e28-11ea-8252-00155d015112",
    isBaseColumn: false,
    dataField: "averageProjectedRate",
    numeric: false,
    label: "Projected Average Rate",
    Cell: ({ record }) => <TableCell>{numberFormatter.formatRate(_.get(record, "averageProjectedRate", null), 2, "\u2014")}</TableCell>
  },
  {
    id: "e0f5b873-3e28-11ea-8252-00155d015112",
    isBaseColumn: false,
    dataField: "loanType.name",
    numeric: false,
    label: "Quote Type",
    Cell: ({ record }) => (
      <TableCell>{record["loanType.name"] || "\u2014\u2014"}</TableCell>
    )
  },
  {
    id: "menu",
    isBaseColumn: true,
    dataField: null,
    layoutOrder: 999,
    numeric: false,
    Cell: ({ record }) => (
      <TableCell className="loansList_Menu" key="menu">
        <LoanTableOptionsMenu
          id={record.id}
          tableName={"WinningQuoteTableColumns"}
        />
      </TableCell>
    )
  }
];

export default WinningQuoteTableColumns;
